/**
 * Coach tier calculation.
 *
 * Tiers are computed client-side from data the dashboard already loads
 * (clients, activities, currentUser). No backend changes required.
 *
 * Ladder (lowest to highest):
 *   NETWORK_MEMBER  — default after signup, no badge shown
 *   VERIFIED        — active Verified Coach or Lifetime subscription
 *                     (subscriptionDetails.subscription >= 2), bronze badge
 *   ESTABLISHED     — 10 clients OR 50 sessions, 90+ days, gold badge
 *   DISTINGUISHED   — 25 clients OR 200 sessions, 6+ months, platinum
 *   MASTER_COACH    — 50 clients OR 500 sessions, 12+ months, diamond
 *
 * Each threshold uses OR logic on clients vs sessions so different
 * practice styles (many short engagements vs few long ones) reach the
 * same tier. Ratings are intentionally NOT gated yet — we don't have
 * reliable rating data on every coach. Add a min-rating clause when
 * the data source is in place.
 */

export const TIERS = {
  NETWORK_MEMBER: 'network_member',
  VERIFIED: 'verified',
  ESTABLISHED: 'established',
  DISTINGUISHED: 'distinguished',
  MASTER_COACH: 'master_coach',
};

// Display order, ascending. Used by Dashboard "Your tier" card to find
// the NEXT tier and its remaining requirements.
export const TIER_ORDER = [
  TIERS.NETWORK_MEMBER,
  TIERS.VERIFIED,
  TIERS.ESTABLISHED,
  TIERS.DISTINGUISHED,
  TIERS.MASTER_COACH,
];

// Tier metadata — labels, colours, requirements, and the benefits each
// tier unlocks. The Dashboard "Your tier" card and the public
// TierBadge component both read from this so labels/colours stay in
// sync across the app.
export const TIER_META = {
  [TIERS.NETWORK_MEMBER]: {
    label: 'Network Member',
    short: 'Member',
    color: '#9CA3AF',
    colorStart: '#9CA3AF',
    colorEnd: '#9CA3AF',
    showBadge: false, // baseline tier — no public badge
    requirements: 'Default after signup',
    unlocks: ['Profile, bookings, payouts'],
  },
  [TIERS.VERIFIED]: {
    label: 'Verified Coach',
    short: 'Verified',
    color: '#C8761A',
    colorStart: '#D88E2D',
    colorEnd: '#A65A0F',
    showBadge: true,
    requirements: 'Profile complete + first paid client',
    unlocks: ['Verified badge on public profile', 'Search ranking lift'],
  },
  [TIERS.ESTABLISHED]: {
    label: 'Established Coach',
    short: 'Established',
    color: '#F59E0B',
    colorStart: '#FBBF24',
    colorEnd: '#D97706',
    showBadge: true,
    requirements: '10 active clients OR 50 delivered sessions · 90+ days on network',
    unlocks: [
      'Established badge on public profile',
      'Featured in marketplace search (top 50% of category)',
      'Access to basic enterprise engagements',
    ],
  },
  [TIERS.DISTINGUISHED]: {
    label: 'Distinguished Coach',
    short: 'Distinguished',
    color: '#94A3B8',
    colorStart: '#E2E8F0',
    colorEnd: '#94A3B8',
    showBadge: true,
    requirements: '25 active clients OR 200 delivered sessions · 6+ months on network',
    unlocks: [
      'Distinguished badge on public profile',
      'Priority search ranking (top 20%)',
      'Full mid-tier enterprise engagement access',
      'Reduced platform fee (−1% on payouts)',
      'Personal account manager',
      'Eligible for Featured Coach of the Month',
    ],
  },
  [TIERS.MASTER_COACH]: {
    label: 'Master Coach',
    short: 'Master',
    color: '#A78BFA',
    colorStart: '#A78BFA',
    colorMid: '#60A5FA',
    colorEnd: '#34D399',
    showBadge: true,
    requirements: '50 active clients OR 500 delivered sessions · 12+ months on network',
    unlocks: [
      'Master Coach badge on public profile (iridescent)',
      'Always-visible search placement (above tier-3)',
      'First-look + exclusive high-value enterprise engagements',
      'Reduced platform fee (−2% on payouts)',
      'Senior account manager',
      'Listed on viveka.world/masters page',
      'Invited to annual closed-network dinner',
      'Direct line to Viveka product leadership',
    ],
  },
};

const MS_PER_DAY = 86400000;

// Minimum number of confirmed client testimonials required for the
// VERIFIED tier. Confirmed = referee clicked the unique link in the
// email Viveka sent (Layer 1 of the verification model).
export const MIN_VERIFIED_TESTIMONIALS = 2;

// Minimum number of approved professional certifications required
// for the VERIFIED tier. Approved = either from the curated
// dropdown (auto-approved on upload) OR admin-approved if it was
// a custom "Other" entry. Coaches can upload unlimited certs; only
// the first qualifies them for the badge.
export const MIN_VERIFIED_CERTIFICATIONS = 1;

/**
 * Sum the "delivered sessions" count across an activities array.
 * Each activity has `activityStatus: [done, total]`. We trust `done`
 * as the count of sessions actually delivered to date.
 */
const sumDeliveredSessions = (activities) => {
  if (!Array.isArray(activities)) return 0;
  return activities.reduce((sum, a) => {
    const status = a?.activityStatus;
    if (!Array.isArray(status) || status.length < 1) return sum;
    const done = parseInt(status[0], 10);
    return sum + (Number.isFinite(done) ? done : 0);
  }, 0);
};

/**
 * Count unique active clients from the clients array. A client is
 * considered "active" if they have at least one program AND their
 * last activity is within the last 180 days. Loose definition — we
 * can tighten if needed.
 */
const countActiveClients = (clients) => {
  if (!Array.isArray(clients)) return 0;
  const cutoff = Date.now() - 180 * MS_PER_DAY;
  return clients.filter((c) => {
    const hasPrograms = Array.isArray(c?.programs) && c.programs.length > 0;
    const lastActivityMs = c?.lastActivity ? new Date(c.lastActivity).getTime() : null;
    const recentlyActive = lastActivityMs && !Number.isNaN(lastActivityMs) && lastActivityMs >= cutoff;
    return hasPrograms || recentlyActive;
  }).length;
};

/**
 * True iff the coach is on a subscription that includes the Viveka
 * Verification badge — the Verified Coach plan or the Lifetime plan
 * (both map to `subscriptionDetails.subscription >= 2`; see
 * planCatalog.js and VerificationBadge.js, which gates the PUBLIC
 * badge the same way). Network Member (1) and unsubscribed (0) do
 * not qualify.
 */
const hasVerifiedSubscription = (currentUser) => {
  const n = currentUser?.attributes?.profile?.metadata?.subscriptionDetails?.subscription;
  return typeof n === 'number' && n >= 2;
};

/**
 * Tenure in days, given the user's Sharetribe createdAt.
 */
const tenureDays = (currentUser) => {
  const createdAt = currentUser?.attributes?.createdAt;
  if (!createdAt) return 0;
  const ms = Date.now() - new Date(createdAt).getTime();
  if (!Number.isFinite(ms)) return 0;
  return Math.floor(ms / MS_PER_DAY);
};

/**
 * Returns the highest tier a coach qualifies for, given their data.
 * Pure function — easy to unit-test once we wire tests up.
 */
export const computeCoachTier = ({ currentUser, clients, activities }) => {
  if (!currentUser) return TIERS.NETWORK_MEMBER;

  const clientsCount = countActiveClients(clients);
  const sessionsDelivered = sumDeliveredSessions(activities);
  const tenure = tenureDays(currentUser);

  // Walk the ladder from highest down, return the first tier the coach
  // qualifies for. Default to NETWORK_MEMBER if none.
  if ((clientsCount >= 50 || sessionsDelivered >= 500) && tenure >= 365) {
    return TIERS.MASTER_COACH;
  }
  if ((clientsCount >= 25 || sessionsDelivered >= 200) && tenure >= 180) {
    return TIERS.DISTINGUISHED;
  }
  if ((clientsCount >= 10 || sessionsDelivered >= 50) && tenure >= 90) {
    return TIERS.ESTABLISHED;
  }
  // Verified is a PAID tier — the Viveka Verification badge is sold as
  // an exclusive feature of the Verified Coach / Lifetime plans (see
  // planCatalog.js's VERIFIED_ONLY_FEATURES). A coach on the Network
  // Member plan must not be shown as Verified just because they picked
  // up a client; they need an active Verified Coach or Lifetime
  // subscription. This is the coach's PLAN tier — used internally
  // (dashboard, billing, entitlements). It is NOT the same as the
  // public BADGE — see computeBadgeTier() below.
  if (hasVerifiedSubscription(currentUser)) {
    return TIERS.VERIFIED;
  }
  return TIERS.NETWORK_MEMBER;
};

/**
 * Read the verifiedTestimonialsCount off the coach's public profile.
 * Source of truth: profile.publicData.verifiedTestimonialsCount,
 * denormalised by the backend testimonial confirm webhook.
 */
const readTestimonialsCount = (user) => {
  const n = user?.attributes?.profile?.publicData?.verifiedTestimonialsCount;
  return typeof n === 'number' && n >= 0 ? n : 0;
};

const readCertificationsCount = (user) => {
  const n = user?.attributes?.profile?.publicData?.verifiedCertificationsCount;
  return typeof n === 'number' && n >= 0 ? n : 0;
};

/**
 * Verification status — used to render dashboard nudges and the
 * "X of 2 testimonials confirmed" progress strip on the testimonials
 * page. Pure derivation, no state.
 */
export const getVerificationStatus = (currentUser) => {
  // Two independent gates: testimonials AND certifications. Both must
  // be met for the public Verified badge to unlock. We surface each
  // separately so the dashboard can show progress on each.
  const testimonialsConfirmed = readTestimonialsCount(currentUser);
  const testimonialsRequired = MIN_VERIFIED_TESTIMONIALS;
  const testimonialsRemaining = Math.max(0, testimonialsRequired - testimonialsConfirmed);
  const testimonialsPassed = testimonialsConfirmed >= testimonialsRequired;

  const certsApproved = readCertificationsCount(currentUser);
  const certsRequired = MIN_VERIFIED_CERTIFICATIONS;
  const certsRemaining = Math.max(0, certsRequired - certsApproved);
  const certsPassed = certsApproved >= certsRequired;

  return {
    // Top-level summary
    badgeUnlocked: testimonialsPassed && certsPassed,

    // Per-gate detail for the dashboard verification panel
    testimonials: {
      confirmed: testimonialsConfirmed,
      required: testimonialsRequired,
      remaining: testimonialsRemaining,
      passed: testimonialsPassed,
    },
    certifications: {
      approved: certsApproved,
      required: certsRequired,
      remaining: certsRemaining,
      passed: certsPassed,
    },

    // Back-compat aliases (callers that only know about the old
    // testimonial-only shape still work)
    confirmed: testimonialsConfirmed,
    required: testimonialsRequired,
    remaining: testimonialsRemaining,
  };
};

/**
 * Returns the tier that should be shown on PUBLIC surfaces (listing
 * cards, profile sidebar, marketplace search). Identical to the plan
 * tier EXCEPT:
 *
 *   - VERIFIED is held back to NETWORK_MEMBER (no public badge) until
 *     the coach has at least MIN_VERIFIED_TESTIMONIALS confirmed
 *     client testimonials.
 *   - Higher tiers (Established+) are not re-gated. Those coaches
 *     have already cleared 10+ active clients, which is itself a
 *     trust signal that doesn't need re-verification retroactively.
 *
 * IMPORTANT: this is for the public BADGE only. The coach's plan,
 * dashboard, billing, and entitlements all keep using computeCoachTier
 * — they paid for it, they keep it.
 */
export const computeBadgeTier = (args) => {
  const planTier = computeCoachTier(args);
  if (planTier === TIERS.VERIFIED) {
    const status = getVerificationStatus(args && args.currentUser);
    if (!status.badgeUnlocked) {
      // Both gates: testimonials (>=2) AND certifications (>=1). If
      // either is missing, withhold the public badge. The plan tier
      // they see in their own dashboard stays Verified.
      return TIERS.NETWORK_MEMBER;
    }
  }
  return planTier;
};

/**
 * Convenience for public TierBadge usage when the only thing in scope
 * is a user object (e.g. on ListingCard, ListingProfileRight). Reads
 * the raw coachTier from publicData and applies the same Verified
 * withhold rule. Use this in any place that renders the public badge.
 */
export const readPublicBadgeTier = (user) => {
  const raw = user?.attributes?.profile?.publicData?.coachTier;
  const storedTier = raw && TIER_ORDER.indexOf(raw) >= 0 ? raw : TIERS.NETWORK_MEMBER;
  // Fall back to the legacy subscription flag when publicData.coachTier
  // was never backfilled — mirrors the backend's resolveCoachTier
  // (viveka-services coach-saas-service). Without this, a coach actively
  // paying for Verified Coach but never graded into publicData.coachTier
  // shows no public badge at all, even though they're entitled to one.
  const effectiveTier =
    storedTier === TIERS.NETWORK_MEMBER && hasVerifiedSubscription(user)
      ? TIERS.VERIFIED
      : storedTier;
  if (effectiveTier === TIERS.VERIFIED) {
    // Public badge requires BOTH testimonials AND certifications gates
    // to pass. See computeBadgeTier() for the same logic.
    const okTestimonials = readTestimonialsCount(user) >= MIN_VERIFIED_TESTIMONIALS;
    const okCerts = readCertificationsCount(user) >= MIN_VERIFIED_CERTIFICATIONS;
    if (!okTestimonials || !okCerts) {
      return TIERS.NETWORK_MEMBER;
    }
  }
  return effectiveTier;
};

/**
 * Given the current tier + metrics, returns the next tier and the
 * single remaining requirement to reach it. Used by the "Your tier"
 * progress card to surface the most actionable next step.
 *
 * Returns null when the coach is already at the top tier.
 */
export const nextTierProgress = ({ currentTier, currentUser, clients, activities }) => {
  const idx = TIER_ORDER.indexOf(currentTier);
  if (idx < 0 || idx >= TIER_ORDER.length - 1) return null;
  const next = TIER_ORDER[idx + 1];

  const clientsCount = countActiveClients(clients);
  const sessionsDelivered = sumDeliveredSessions(activities);
  const tenure = tenureDays(currentUser);

  // For each next tier, surface the SHORTEST gap as the "next action"
  // — that's what the coach can move the needle on fastest.
  const gaps = {
    [TIERS.VERIFIED]: [
      {
        label: 'Verified Coach subscription',
        current: hasVerifiedSubscription(currentUser) ? 1 : 0,
        target: 1,
        // Verified is a subscribe/don't-subscribe gate, not a count to
        // tick up — "1 more Verified Coach subscription" reads oddly
        // through the generic "N more {label}" template below, so this
        // gap supplies its own sentence.
        customText: 'Subscribe to Verified Coach or Lifetime to reach',
      },
    ],
    [TIERS.ESTABLISHED]: [
      { label: 'active clients', current: clientsCount, target: 10 },
      { label: 'delivered sessions', current: sessionsDelivered, target: 50 },
      { label: 'days on network', current: tenure, target: 90 },
    ],
    [TIERS.DISTINGUISHED]: [
      { label: 'active clients', current: clientsCount, target: 25 },
      { label: 'delivered sessions', current: sessionsDelivered, target: 200 },
      { label: 'days on network', current: tenure, target: 180 },
    ],
    [TIERS.MASTER_COACH]: [
      { label: 'active clients', current: clientsCount, target: 50 },
      { label: 'delivered sessions', current: sessionsDelivered, target: 500 },
      { label: 'days on network', current: tenure, target: 365 },
    ],
  };

  const nextGaps = gaps[next] || [];
  // Find the shortest gap (excluding ones already met). Tenure-only gaps
  // present "you need to wait" framing which is less actionable, so we
  // de-prioritise them when a client/session metric is also remaining.
  const remaining = nextGaps
    .filter((g) => g.current < g.target)
    .sort((a, b) => {
      // Prefer client/session gaps over time gaps.
      const aIsTime = a.label.includes('days');
      const bIsTime = b.label.includes('days');
      if (aIsTime !== bIsTime) return aIsTime ? 1 : -1;
      // Otherwise smallest remaining wins.
      return (a.target - a.current) - (b.target - b.current);
    });

  const topGap = remaining[0] || null;

  return {
    nextTier: next,
    nextTierLabel: TIER_META[next].label,
    progress: topGap
      ? {
          label: topGap.label,
          current: Math.min(topGap.current, topGap.target),
          target: topGap.target,
          pct: Math.min(100, Math.round((topGap.current / topGap.target) * 100)),
          customText: topGap.customText,
        }
      : null,
  };
};

/**
 * Read a coach's stored tier from a Sharetribe user object.
 *
 * For non-current-user coaches we can't recompute tier locally — we
 * don't have their full clients/activities data. Instead we read a
 * `coachTier` string from `user.attributes.profile.publicData`. The
 * Viveka team can seed this manually via Sharetribe Console for the
 * First 100 cohort; a scheduled job will compute and write it later.
 *
 * Defaults to NETWORK_MEMBER (which renders no badge) so missing data
 * never breaks the layout.
 *
 * Accepts either a Sharetribe user entity (with .attributes.profile)
 * or a listing.author object — both have the same shape.
 */
export const readCoachTier = (userOrAuthor) => {
  const tier =
    userOrAuthor?.attributes?.profile?.publicData?.coachTier;
  // Guard against typos / unknown values from the console.
  if (tier && TIER_ORDER.indexOf(tier) >= 0) return tier;
  return TIERS.NETWORK_MEMBER;
};

/**
 * Read a coach's PLAN tier — what they've paid for or earned by
 * activity. This is the entitlement-side read.
 *
 * IMPORTANT: this is different from `readPublicBadgeTier`. The badge
 * version withholds the Verified badge from coaches who haven't yet
 * confirmed 2+ testimonials and 1+ certification. The PLAN version
 * does NOT withhold — if a coach pays for Verified (or earns it by
 * activity), they get the entitlements (Coach Branding, Verification
 * tab, Reports, programs-without-cap, ads-without-cap) immediately,
 * even before their public badge unlocks. Badge is about trust
 * signal, plan is about what they paid for.
 *
 * Defaults to NETWORK_MEMBER so a new signup that hasn't been graded
 * yet is treated as the baseline tier.
 */
export const readPlanTier = (userOrAuthor) => {
  // Same source-of-truth as readCoachTier but kept as a separate
  // function so future tier-vs-badge divergence stays explicit at
  // every call site.
  return readCoachTier(userOrAuthor);
};

/**
 * Map the legacy numeric subscription tier
 * (`metadata.subscriptionDetails.subscription`) to our string tier
 * vocabulary. Two parallel tier systems coexist:
 *
 *   • New:    `publicData.coachTier` (string) — set by tier calc job
 *   • Legacy: `metadata.subscriptionDetails.subscription` (numeric)
 *     0 = no subscription / cancelled / expired
 *     1 = Network Member
 *     2 = Verified Coach / Lifetime
 *
 * Reads BOTH the new `publicData.coachTier` and the legacy
 * `metadata.subscriptionDetails.subscription`, picks whichever is
 * higher. This keeps Lifetime / Verified subscribers who haven't yet
 * been graded into `coachTier` from being blocked. Once the
 * publicData backfill is complete and every coach has a `coachTier`
 * value, the legacy fallback can be removed.
 *
 * Uses TIER_ORDER index comparison so the function stays correct
 * even when we add or remove tiers between the existing ones.
 */
const readLegacySubscriptionTier = (user) => {
  const n = user?.attributes?.profile?.metadata?.subscriptionDetails?.subscription;
  if (typeof n !== 'number') return TIERS.NETWORK_MEMBER;
  if (n >= 2) return TIERS.VERIFIED;
  if (n >= 1) return TIERS.NETWORK_MEMBER;
  return TIERS.NETWORK_MEMBER;
};

export const meetsTierGate = (userOrAuthor, minTier) => {
  const newTier = readPlanTier(userOrAuthor);
  const legacyTier = readLegacySubscriptionTier(userOrAuthor);
  const newIdx = TIER_ORDER.indexOf(newTier);
  const legacyIdx = TIER_ORDER.indexOf(legacyTier);
  const userIdx = Math.max(newIdx, legacyIdx);
  const minIdx = TIER_ORDER.indexOf(minTier);
  if (userIdx < 0 || minIdx < 0) return false;
  return userIdx >= minIdx;
};

/**
 * Returns the next tier above the user's current plan tier — what
 * they'd need to upgrade to. Used by UpgradeGate to show "Upgrade
 * to {nextTierLabel}" CTAs. Returns null if user is already at the
 * top tier.
 */
export const nextPlanTier = (userOrAuthor) => {
  const current = readPlanTier(userOrAuthor);
  const idx = TIER_ORDER.indexOf(current);
  if (idx < 0 || idx >= TIER_ORDER.length - 1) return null;
  return TIER_ORDER[idx + 1];
};
